import { CalendarOffIcon } from '@/components/atoms/icons';
import { ContentDialog } from '@/components/molecules/dialogs/ContentDialog';
import { useAutoCloseOnVisible, useUIColors } from '@/core/hooks';
import { isSmallScreen } from '@/core/utils/responsive';
import React from 'react';
import { Platform, StyleSheet, View, type ViewStyle } from 'react-native';
import { usePropertyLabels } from '../hooks/usePropertyLabels';

export interface ReservationCanceledDialogProps {
  visible: boolean;
  onDismiss: () => void;
  /** Auto-dismiss delay; defaults to 3000ms */
  autoCloseDelayMs?: number;
}

/** Figma reservation-canceled content frame (desktop) */
const DIALOG_WIDTH = 465;
const DIALOG_HEIGHT = 210;
const ICON_FRAME = 80;
const ICON_SIZE = 66.67;

/** Mobile Figma: 236×185, gap 24, radius 16, padding Spacing/6 (24) */
const MOBILE_DIALOG_WIDTH = 236;
const MOBILE_DIALOG_HEIGHT = 185;
const MOBILE_DIALOG_GAP = 24;
const MOBILE_ICON_SIZE = 40;
const MOBILE_ICON_FRAME = 40;

const webBorderBox: ViewStyle =
  Platform.OS === 'web'
    ? ({ boxSizing: 'border-box' } as ViewStyle)
    : {};

/**
 * Success after canceling a unit reservation (web properties).
 * Desktop: 465×210, gap 16 — icon + title + message; dismiss on backdrop.
 * Mobile: 236×185, gap 24, padding 24.
 * Auto-closes after `autoCloseDelayMs`.
 */
export function ReservationCanceledDialog({
  visible,
  onDismiss,
  autoCloseDelayMs = 3000,
}: Readonly<ReservationCanceledDialogProps>) {
  const colors = useUIColors();
  const { UNIT_DETAILS: labels } = usePropertyLabels();
  const isMobile = isSmallScreen();
  const iconSize = isMobile ? MOBILE_ICON_SIZE : ICON_SIZE;
  useAutoCloseOnVisible(visible, onDismiss, autoCloseDelayMs);

  return (
    <ContentDialog
      visible={visible}
      onClose={onDismiss}
      contentAlign="center"
      maxWidth={isMobile ? MOBILE_DIALOG_WIDTH : DIALOG_WIDTH}
      cardStyle={[
        styles.card,
        isMobile ? styles.cardMobile : styles.cardDesktop,
        webBorderBox,
        { backgroundColor: colors.backgroundLevel1 },
      ]}
      headerStyle={[styles.header, isMobile ? styles.headerMobile : null]}
      titleStyle={isMobile ? styles.titleMobile : styles.title}
      subtitleStyle={isMobile ? styles.subtitleMobile : styles.subtitle}
      icon={
        <View style={isMobile ? styles.iconFrameMobile : styles.iconFrame}>
          <CalendarOffIcon size={iconSize} color={colors.fontPrimary} />
        </View>
      }
      title={labels.RESERVATION_CANCELED_TITLE}
      titleVariant={isMobile ? 'headline' : 'title2'}
      titleColor={isMobile ? 'secondary' : 'primary'}
      subtitle={labels.RESERVATION_CANCELED_MESSAGE}
      subtitleVariant="body"
      subtitleColor="secondary"
      closeOnBackdropPress
    />
  );
}

const styles = StyleSheet.create({
  card: {
    width: '100%',
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  cardDesktop: {
    maxWidth: DIALOG_WIDTH,
    height: DIALOG_HEIGHT,
    maxHeight: DIALOG_HEIGHT,
    gap: 16,
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0,
  },
  cardMobile: {
    width: MOBILE_DIALOG_WIDTH,
    maxWidth: MOBILE_DIALOG_WIDTH,
    height: MOBILE_DIALOG_HEIGHT,
    maxHeight: MOBILE_DIALOG_HEIGHT,
    gap: MOBILE_DIALOG_GAP,
    padding: 24,
  },
  header: {
    gap: 16,
    alignItems: 'center',
    width: '100%',
    justifyContent: 'center',
  },
  headerMobile: {
    gap: 8,
  },
  iconFrame: {
    width: ICON_FRAME,
    height: ICON_FRAME,
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconFrameMobile: {
    width: MOBILE_ICON_FRAME,
    height: MOBILE_ICON_FRAME,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    /** Icon → “Reservation Canceled” */
    marginBottom: 16,
  },
  /** Reservation Canceled — Size/2XL Medium, LH 100%, centered */
  title: {
    lineHeight: 22,
    textAlign: 'center',
    fontWeight: '500',
  },
  /** Mobile — Font/Size/Base SemiBold, LH 100%, centered */
  titleMobile: {
    fontSize: 16,
    fontFamily: 'Matter-SemiBold',
    lineHeight: 16,
    textAlign: 'center',
  },
  subtitle: {
    textAlign: 'center',
    alignSelf: 'center',
    width: '100%',
    lineHeight: 22,
  },
  /** Mobile — wrap inside padded card so copy isn’t cropped */
  subtitleMobile: {
    textAlign: 'center',
    alignSelf: 'center',
    width: '100%',
    fontSize: 14,
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    lineHeight: 21,
    flexShrink: 1,
    ...(Platform.OS === 'web'
      ? ({ whiteSpace: 'normal' } as ViewStyle)
      : null),
  },
});

export default ReservationCanceledDialog;
